import React, { useState } from "react";
import { Camera, ChevronDown, ChevronUp, Clock, Sliders } from "lucide-react";

const LORA  = { fontFamily: "'Lora', Georgia, serif" };
const SERIF = { fontFamily: "'Cormorant Garamond', Georgia, serif" };

function SpotEntry({ spot, index }) {
  const [open, setOpen] = useState(index === 0); // first spot open by default

  return (
    <div
      className="border border-[#DCD4C6] bg-[#FDFAF7] overflow-hidden"
      style={{ borderRadius: "3px" }}
      data-testid={`photo-spot-${index}`}
    >
      {/* Spot header — always visible */}
      <button
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#F9F6F0] transition-colors"
        onClick={() => setOpen(o => !o)}
        data-testid={`photo-spot-toggle-${index}`}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <Camera size={13} color="#C17F59" strokeWidth={1.5} className="flex-shrink-0" />
          <span
            className="text-sm font-semibold text-[#2B2623] truncate"
            style={SERIF}
          >
            {spot.locationName}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
          <span
            className="hidden sm:inline-flex items-center gap-1 text-xs text-[#8B5E34] bg-[#F1EBE1] border border-[#DCD4C6] px-2 py-0.5"
            style={{ borderRadius: "2px", ...LORA }}
          >
            <Clock size={9} strokeWidth={2} />
            {spot.bestTime.split("·")[0].trim()}
          </span>
          {open
            ? <ChevronUp size={13} color="#9A8F86" strokeWidth={1.5} />
            : <ChevronDown size={13} color="#9A8F86" strokeWidth={1.5} />
          }
        </div>
      </button>

      {/* Spot details — collapsible */}
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-[#F1EBE1]">

          {/* Best time */}
          <div className="flex items-start gap-2 pt-3">
            <Clock size={13} color="#C17F59" strokeWidth={1.5} className="flex-shrink-0 mt-0.5" />
            <p className="text-xs text-[#5C554E]" style={LORA}>
              <strong className="text-[#2B2623]">Meilleure heure :</strong> {spot.bestTime}
            </p>
          </div>

          {/* Camera settings — monospace style */}
          <div className="flex items-start gap-2">
            <Sliders size={13} color="#C17F59" strokeWidth={1.5} className="flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs text-[#8B5E34] uppercase tracking-widest mb-1" style={LORA}>
                Réglages
              </p>
              <p
                className="text-xs text-[#2B2623] leading-relaxed bg-[#F9F6F0] border border-[#DCD4C6] px-2.5 py-2 whitespace-pre-line"
                style={{ fontFamily: "'Courier New', monospace", borderRadius: "2px", letterSpacing: "0.01em" }}
                data-testid={`photo-settings-${index}`}
              >
                {spot.phoneSettings}
              </p>
            </div>
          </div>

          {/* Diego's tip */}
          <div
            className="border-l-2 border-[#C17F59] pl-3 py-0.5"
          >
            <p className="text-xs text-[#9A8F86] uppercase tracking-widest mb-1" style={LORA}>
              Conseil de Diego
            </p>
            <p
              className="text-xs text-[#5C554E] italic leading-relaxed"
              style={LORA}
              data-testid={`photo-tip-${index}`}
            >
              « {spot.diegosTip} »
            </p>
          </div>

        </div>
      )}
    </div>
  );
}

/**
 * PhotoSpotCard — shows 1-2 relevant photo spots for the current itinerary day.
 * Returns null when no spots matched (invisible if irrelevant).
 */
export function PhotoSpotCard({ spots }) {
  if (!spots || spots.length === 0) return null;

  return (
    <div
      className="mt-4 pt-4 border-t border-[#F1EBE1]"
      data-testid="photo-spot-section"
    >
      {/* Section header */}
      <div className="flex items-center gap-2 mb-3">
        <div
          className="w-6 h-6 bg-[#F1EBE1] border border-[#DCD4C6] flex items-center justify-center flex-shrink-0"
          style={{ borderRadius: "2px" }}
        >
          <Camera size={12} color="#C17F59" strokeWidth={1.5} />
        </div>
        <p className="text-xs uppercase tracking-widest text-[#8B5E34]" style={LORA}>
          Spots Photo & Conseils Caméra
        </p>
        <span
          className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5"
          style={{ ...LORA, borderRadius: "2px" }}
        >
          Hors-ligne
        </span>
      </div>

      {/* Spot cards */}
      <div className="space-y-2">
        {spots.map((spot, i) => (
          <SpotEntry key={i} spot={spot} index={i} />
        ))}
      </div>
    </div>
  );
}
